/*
 This file is part of GNU Taler
 (C) 2022-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */
import {
  assertUnreachable,
  ChallengerApi,
  EmptyObject,
  HttpStatusCode,
  InternationalizationAPI,
  TalerErrorCode,
  TalerError,
  TalerFormAttributes,
  TranslatedString,
} from "@gnu-taler/taler-util";
import {
  countryNameList,
  FormDesign,
  FormUI,
  RouteDefinition,
  useChallengerApiContext,
  useForm,
  useAsyncAction,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { Fragment, h, VNode } from "preact";
import { useState } from "preact/hooks";
import {
  ActionButton,
  formatErrorDetails,
  formatHttpErrorDetails,
  InlineNotice,
  LoadingState,
  SecondaryAction,
  TechnicalDetails,
  VerificationCard,
} from "../components/VerificationUi.js";
import { useChallengeSession } from "../hooks/challenge.js";
import { SessionId, useSessionState } from "../hooks/session.js";
import { getAddressDescriptionFromAddrType } from "./AnswerChallenge.js";

type Props = {
  onSendSuccesful: () => void;
  onComplete: () => void;
  session: SessionId;
  routeSolveChallenge: RouteDefinition<EmptyObject>;
  changeOnly?: boolean;
  focus?: boolean;
};

export type AddressEntryMode = "initial" | "change" | "locked";

export function getAddressEntryMode(
  status: Pick<
    ChallengerApi.ChallengeStatus,
    "last_address" | "fix_address" | "changes_left"
  >,
  changeOnly: boolean,
): AddressEntryMode {
  if (!changeOnly) return "initial";
  if (!status.last_address) return "locked";
  if (status.fix_address || status.changes_left < 1) return "locked";
  return "change";
}

function addressFieldNames(
  type: ChallengerApi.ChallengerTermsOfServiceResponse["address_type"],
): string[] {
  switch (type) {
    case "email":
      return [TalerFormAttributes.CONTACT_EMAIL];
    case "phone":
      return [TalerFormAttributes.CONTACT_PHONE];
    case "postal":
    case "postal-ch":
      return [
        TalerFormAttributes.CONTACT_NAME,
        TalerFormAttributes.ADDRESS_LINES,
        TalerFormAttributes.ADDRESS_COUNTRY,
      ];
    default:
      assertUnreachable(type);
  }
}

export function isSameAddressForType(
  type: ChallengerApi.ChallengerTermsOfServiceResponse["address_type"],
  previous: Record<string, string>,
  candidate: Record<string, string>,
): boolean {
  return addressFieldNames(type).every(
    (field) => previous[field] === candidate[field],
  );
}

export function canSubmitDestination(
  mode: AddressEntryMode,
  type: ChallengerApi.ChallengerTermsOfServiceResponse["address_type"],
  previous: Record<string, string> | undefined,
  candidate: Record<string, string> | undefined,
): boolean {
  if (!candidate || mode === "locked") return false;
  if (mode === "initial") return true;
  return !!previous && !isSameAddressForType(type, previous, candidate);
}

export function getAddressDisplayLines(
  type: ChallengerApi.ChallengerTermsOfServiceResponse["address_type"],
  address: Record<string, string>,
  countryLabel?: string,
): string[] {
  switch (type) {
    case "email":
      return [address[TalerFormAttributes.CONTACT_EMAIL]].filter(
        (line): line is string => !!line,
      );
    case "phone":
      return [address[TalerFormAttributes.CONTACT_PHONE]].filter(
        (line): line is string => !!line,
      );
    case "postal":
    case "postal-ch":
      return [
        address[TalerFormAttributes.CONTACT_NAME],
        ...(address[TalerFormAttributes.ADDRESS_LINES]?.split(/\r?\n/) ?? []),
        countryLabel ?? address[TalerFormAttributes.ADDRESS_COUNTRY],
      ].filter((line): line is string => !!line);
    default:
      assertUnreachable(type);
  }
}

export function AskChallenge(props: Props): VNode {
  const { i18n } = useTranslationContext();
  const { config } = useChallengerApiContext();
  const { result, retry } = useChallengeSession(props.session);
  // Translators: Loading title while Challenger retrieves any previously
  // entered email address, phone number, or postal address.
  const loadingContactTitle = i18n.str`Loading contact details…`;
  // Translators: Error-notice heading when the address-entry screen cannot load
  // the user's current verification session.
  const loadFailureTitle = i18n.str`We could not load your verification`;

  if (!result) {
    return <LoadingState title={loadingContactTitle} />;
  }
  if (result instanceof TalerError) {
    const technicalDetails = formatErrorDetails(result);
    return (
      <VerificationCard title={i18n.str`Verification service unavailable`}>
        <InlineNotice tone="error" title={loadFailureTitle}>
          <i18n.Translate>
            Check your connection and try again. Your progress is safe.
          </i18n.Translate>
        </InlineNotice>
        <div class="mt-5">
          <ActionButton
            busyLabel={i18n.str`Loading…`}
            onClick={async () => {
              await retry();
            }}
          >
            <i18n.Translate>Try again</i18n.Translate>
          </ActionButton>
        </div>
        <TechnicalDetails copyText={technicalDetails}>
          {technicalDetails}
        </TechnicalDetails>
      </VerificationCard>
    );
  }
  if (result.type === "fail") {
    // Translators: Page title when Challenger rejects the request to load the
    // address-entry step of an existing verification.
    const detailsFailureTitle = i18n.str`Could not load verification details`;
    // Translators: Error-notice heading when the current verification session
    // cannot be used.
    const unavailableTitle = i18n.str`Verification unavailable`;
    // Translators: Recovery instruction for an expired verification session.
    const expiredDescription = i18n.str`This verification has expired. Return to the application and start again.`;
    // Translators: Recovery instruction after all code-entry attempts have been
    // exhausted for the current verification session.
    const exhaustedDescription = i18n.str`No attempts remain. Return to the application and start a new verification.`;
    // Translators: Generic explanation when Challenger rejects the request to
    // load a verification session.
    const rejectedDescription = i18n.str`The service rejected this verification request.`;
    const technicalDetails = formatHttpErrorDetails(result);
    return (
      <VerificationCard title={detailsFailureTitle}>
        <InlineNotice tone="error" title={unavailableTitle}>
          {result.case === HttpStatusCode.NotFound
            ? expiredDescription
            : result.case === HttpStatusCode.TooManyRequests
              ? exhaustedDescription
              : rejectedDescription}
        </InlineNotice>
        <TechnicalDetails copyText={technicalDetails}>
          {technicalDetails}
        </TechnicalDetails>
      </VerificationCard>
    );
  }
  const invalidRestriction = findInvalidRestriction(config.restrictions ?? {});
  if (invalidRestriction) {
    // Translators: %1$s is the name of a Challenger configuration field whose
    // regular-expression validation rule is invalid.
    const invalidRule = i18n.str`Invalid validation rule: ${invalidRestriction}`;
    // Translators: Error-notice heading for a problem in the Challenger
    // administrator's address-validation configuration.
    const configurationErrorTitle = i18n.str`Service configuration error`;
    // Translators: User-facing explanation for a server configuration problem;
    // “address” may mean an email address, phone number, or postal address.
    const configurationErrorDescription = i18n.str`The service cannot validate this address right now. Please return to the application and try again later.`;
    return (
      <VerificationCard title={i18n.str`Verification service unavailable`}>
        <InlineNotice tone="error" title={configurationErrorTitle}>
          {configurationErrorDescription}
        </InlineNotice>
        <TechnicalDetails copyText={invalidRule}>
          {invalidRule}
        </TechnicalDetails>
      </VerificationCard>
    );
  }
  return <AskChallengeInternal {...props} lastStatus={result.body} />;
}

function AskChallengeInternal({
  onSendSuccesful,
  onComplete,
  routeSolveChallenge,
  session,
  changeOnly = false,
  focus,
  lastStatus,
}: Props & { lastStatus: ChallengerApi.ChallengeStatus }): VNode {
  const { sent, completed } = useSessionState(session);
  const { lib, config } = useChallengerApiContext();
  const { i18n, lang } = useTranslationContext();
  const [actionError, setActionError] = useState<TranslatedString>();
  const [actionErrorDetails, setActionErrorDetails] = useState<string>();
  // Translators: Network-error message shown after an attempt to send a
  // verification code through Challenger.
  const connectionFailureMessage = i18n.str`We could not contact the verification service. Check your connection and try again.`;

  const initial = { ...(lastStatus.last_address ?? {}) };
  if (config.address_type === "postal-ch") {
    initial[TalerFormAttributes.ADDRESS_COUNTRY] = "CH";
  }

  const mode = getAddressEntryMode(lastStatus, changeOnly);

  const design = getFormDesignBasedOnAddressType(
    i18n,
    lang,
    config.address_type,
    config.restrictions ?? {},
    lastStatus.fix_address,
    mode === "change",
  );
  const form = useForm(design, initial);

  const prevAddr = !lastStatus?.last_address
    ? undefined
    : getAddressDescriptionFromAddrType(
        config.address_type,
        lastStatus.last_address,
      );

  const contact =
    form.status.status === "fail"
      ? undefined
      : (form.status.result as Record<string, string>);
  const previousAddress = lastStatus.last_address;
  const unchanged =
    mode === "change" &&
    !!previousAddress &&
    !!contact &&
    isSameAddressForType(config.address_type, previousAddress, contact);
  const sendArgs = canSubmitDestination(
    mode,
    config.address_type,
    previousAddress,
    contact,
  )
    ? ([session.nonce, contact!] as const)
    : undefined;
  const send = useAsyncAction<
    Awaited<ReturnType<typeof lib.challenger.challenge>>,
    [string, Record<string, string>]
  >((ct, n, i) => lib.challenger.challenge(n, i), {
    onResult(result) {
      if (result.type === "ok") {
        setActionError(undefined);
        setActionErrorDetails(undefined);
        if (result.body.type === "completed") {
          completed(result.body);
          onComplete();
        } else {
          sent(result.body);
          onSendSuccesful();
        }
        return;
      }
      setActionError(challengeFailureMessage(i18n, result));
      setActionErrorDetails(formatHttpErrorDetails(result));
    },
    onError(error) {
      setActionError(connectionFailureMessage);
      setActionErrorDetails(formatErrorDetails(error));
    },
  });

  // Translators: Heading for verifying the email address where the one-time
  // code will be delivered.
  const emailTitle = i18n.str`Verify your email address`;
  // Translators: Heading for verifying the phone number where the one-time
  // code will be delivered.
  const phoneTitle = i18n.str`Verify your phone number`;
  // Translators: Heading for verifying the postal address where a letter
  // containing the one-time code will be delivered.
  const postalTitle = i18n.str`Verify your postal address`;
  // Translators: Description below the email-verification heading explaining
  // how the one-time code will be delivered.
  const emailDescription = i18n.str`We will email you a one-time verification code.`;
  // Translators: Description below the phone-verification heading explaining
  // that the one-time code will arrive in a text message.
  const phoneDescription = i18n.str`We will send you a one-time verification code by SMS.`;
  // Translators: Description below the postal-verification heading explaining
  // that a physical letter will contain the one-time code.
  const postalDescription = i18n.str`We will send you a letter containing a one-time verification code.`;
  // Translators: Heading shown after the user chooses to replace the email
  // address that received the current verification code.
  const changeEmailTitle = i18n.str`Change your email address`;
  // Translators: Heading shown after the user chooses to replace the phone
  // number that received the current verification code.
  const changePhoneTitle = i18n.str`Change your phone number`;
  // Translators: Heading shown after the user chooses to replace the postal
  // address that received the current verification letter.
  const changePostalTitle = i18n.str`Change your postal address`;
  // Translators: Instruction above a replacement email-address form. A new
  // verification code will be sent after the form is submitted.
  const changeEmailDescription = i18n.str`Enter the email address where you want to receive a new verification code.`;
  // Translators: Instruction above a replacement phone-number form. A new
  // verification code will be sent after the form is submitted.
  const changePhoneDescription = i18n.str`Enter the phone number where you want to receive a new verification code.`;
  // Translators: Instruction above a replacement postal-address form. A new
  // verification letter will be sent after the form is submitted.
  const changePostalDescription = i18n.str`Enter the postal address where you want to receive a new verification code.`;
  const initialTitle =
    config.address_type === "email"
      ? emailTitle
      : config.address_type === "phone"
        ? phoneTitle
        : postalTitle;
  const initialDescription =
    config.address_type === "email"
      ? emailDescription
      : config.address_type === "phone"
        ? phoneDescription
        : postalDescription;
  const changeTitle =
    config.address_type === "email"
      ? changeEmailTitle
      : config.address_type === "phone"
        ? changePhoneTitle
        : changePostalTitle;
  const changeDescription =
    config.address_type === "email"
      ? changeEmailDescription
      : config.address_type === "phone"
        ? changePhoneDescription
        : changePostalDescription;
  // Translators: Label above the email address that received the current
  // verification code, before the user enters a replacement.
  const currentEmailLabel = i18n.str`Current email address`;
  // Translators: Label above the phone number that received the current
  // verification code, before the user enters a replacement.
  const currentPhoneLabel = i18n.str`Current phone number`;
  // Translators: Label above the postal address that received the current
  // verification letter, before the user enters a replacement.
  const currentPostalLabel = i18n.str`Current postal address`;
  const currentAddressLabel =
    config.address_type === "email"
      ? currentEmailLabel
      : config.address_type === "phone"
        ? currentPhoneLabel
        : currentPostalLabel;
  // Translators: Section heading above the fields for a replacement postal
  // address. The current postal address is displayed immediately before it.
  const newPostalAddressLabel = i18n.str`New postal address`;
  // Translators: Warning heading when the user can replace their email
  // address, phone number, or postal address only once more.
  const lastChangeTitle = i18n.str`Last available change`;
  // Translators: Warning that submitting a replacement email address will use
  // the final permitted address change for this verification.
  const oneEmailChangeLeft = i18n.str`You can change the email address one more time.`;
  // Translators: Warning that submitting a replacement phone number will use
  // the final permitted address change for this verification.
  const onePhoneChangeLeft = i18n.str`You can change the phone number one more time.`;
  // Translators: Warning that submitting a replacement postal address will use
  // the final permitted address change for this verification.
  const onePostalChangeLeft = i18n.str`You can change the postal address one more time.`;
  const oneChangeLeft =
    config.address_type === "email"
      ? oneEmailChangeLeft
      : config.address_type === "phone"
        ? onePhoneChangeLeft
        : onePostalChangeLeft;
  // Translators: Help text below a prefilled replacement-address form. The
  // primary action stays disabled until at least one field is edited.
  const unchangedAddressHelp = i18n.str`Change at least one field to continue.`;
  // Translators: Supporting text beside the secondary action that returns to
  // an earlier, still-usable verification code. %1$s is the email address,
  // phone number, or postal addressee that received it.
  const previousCodeStillValid = i18n.str`The previous code sent to ${prevAddr ?? ""} can still be used.`;
  // Translators: Secondary link that cancels changing the email address, phone
  // number, or postal address and returns to verification-code entry.
  const backToCodeLabel = i18n.str`Back to code entry`;
  // Translators: Warning heading shown when the verification no longer permits
  // replacing its email address, phone number, or postal address.
  const noChangesTitle = i18n.str`No changes remaining`;
  // Translators: Page title when an email address is fixed or all permitted
  // email-address changes have been used.
  const emailLockedTitle = i18n.str`Email address cannot be changed`;
  // Translators: Page title when a phone number is fixed or all permitted
  // phone-number changes have been used.
  const phoneLockedTitle = i18n.str`Phone number cannot be changed`;
  // Translators: Page title when a postal address is fixed or all permitted
  // postal-address changes have been used.
  const postalLockedTitle = i18n.str`Postal address cannot be changed`;
  const lockedTitle =
    config.address_type === "email"
      ? emailLockedTitle
      : config.address_type === "phone"
        ? phoneLockedTitle
        : postalLockedTitle;
  // Translators: Explanation that the email address for this verification is
  // fixed or that all permitted email-address changes have been used.
  const emailLockedDescription = i18n.str`This email address cannot be changed.`;
  // Translators: Explanation that the phone number for this verification is
  // fixed or that all permitted phone-number changes have been used.
  const phoneLockedDescription = i18n.str`This phone number cannot be changed.`;
  // Translators: Explanation that the postal address for this verification is
  // fixed or that all permitted postal-address changes have been used.
  const postalLockedDescription = i18n.str`This postal address cannot be changed.`;
  const lockedDescription =
    config.address_type === "email"
      ? emailLockedDescription
      : config.address_type === "phone"
        ? phoneLockedDescription
        : postalLockedDescription;
  // Translators: Recovery instruction when neither the current verification
  // code nor a replacement address can be used.
  const restartDescription = i18n.str`Return to the application and start a new verification.`;
  // Translators: Error-notice heading after Challenger fails to send a
  // verification code.
  const sendFailureTitle = i18n.str`Code could not be sent`;
  // Translators: Busy-state label on the button while Challenger sends a
  // verification code.
  const sendingCodeLabel = i18n.str`Sending code…`;
  // Translators: Primary button that sends a one-time verification code to the
  // entered email address, phone number, or postal address.
  const sendCodeLabel = i18n.str`Send verification code`;
  // Translators: Primary button that sends a verification code to a replacement
  // email address entered by the user.
  const sendToNewEmailLabel = i18n.str`Send code to new email address`;
  // Translators: Primary button that sends a verification code to a replacement
  // phone number entered by the user.
  const sendToNewPhoneLabel = i18n.str`Send code to new phone number`;
  // Translators: Primary button that sends a verification letter to a
  // replacement postal address entered by the user.
  const sendToNewPostalLabel = i18n.str`Send code to new postal address`;
  const changeActionLabel =
    config.address_type === "email"
      ? sendToNewEmailLabel
      : config.address_type === "phone"
        ? sendToNewPhoneLabel
        : sendToNewPostalLabel;
  const title =
    mode === "initial"
      ? initialTitle
      : mode === "change"
        ? changeTitle
        : lockedTitle;
  const description =
    mode === "initial"
      ? initialDescription
      : mode === "change"
        ? changeDescription
        : undefined;
  const countryCode = previousAddress?.[TalerFormAttributes.ADDRESS_COUNTRY];
  const countryLabel = countryCode
    ? countryNameList(i18n).find((choice) => choice.value === countryCode)
        ?.label
    : undefined;
  const currentAddressLines = previousAddress
    ? getAddressDisplayLines(config.address_type, previousAddress, countryLabel)
    : [];
  const canUsePreviousCode = !!prevAddr && lastStatus.auth_attempts_left > 0;

  return (
    <VerificationCard step="details" title={title} description={description}>
      {previousAddress && mode !== "initial" ? (
        <div class="rounded-lg border border-outlineVariant bg-secondaryContainer p-4 text-onSecondaryContainer dark:border-darkSecondaryContainer dark:bg-darkSecondaryContainer dark:text-darkOnSecondaryContainer">
          <p class="text-sm font-semibold">{currentAddressLabel}</p>
          <div class="mt-1 break-words">
            {currentAddressLines.map((line, index) => (
              <p key={`${index}:${line}`}>{line}</p>
            ))}
          </div>
        </div>
      ) : undefined}

      {mode === "locked" ? (
        <div class="mt-5">
          <InlineNotice tone="warning" title={noChangesTitle}>
            {lockedDescription}
            {!canUsePreviousCode ? (
              <span> {restartDescription}</span>
            ) : undefined}
          </InlineNotice>
          {canUsePreviousCode ? (
            <div class="mt-4">
              <SecondaryAction href={routeSolveChallenge.url({})}>
                {backToCodeLabel}
              </SecondaryAction>
            </div>
          ) : undefined}
        </div>
      ) : (
        <>
          {mode === "change" && lastStatus.changes_left === 1 ? (
            <div class="mt-5">
              <InlineNotice tone="warning" title={lastChangeTitle}>
                {oneChangeLeft}
              </InlineNotice>
            </div>
          ) : undefined}

          <div class={mode === "change" ? "mt-5" : undefined}>
            {mode === "change" &&
            (config.address_type === "postal" ||
              config.address_type === "postal-ch") ? (
              <p class="mb-2 text-sm font-semibold">{newPostalAddressLabel}</p>
            ) : undefined}
            <FormUI
              design={design}
              model={form.model}
              onSubmit={sendArgs ? () => send.run(...sendArgs) : undefined}
            />
          </div>

          {unchanged ? (
            <p class="mt-3 text-sm text-secondary dark:text-darkSecondary">
              {unchangedAddressHelp}
            </p>
          ) : undefined}

          {actionError ? (
            <div class="mt-5">
              <InlineNotice tone="error" title={sendFailureTitle}>
                {actionError}
              </InlineNotice>
              {actionErrorDetails ? (
                <TechnicalDetails copyText={actionErrorDetails}>
                  {actionErrorDetails}
                </TechnicalDetails>
              ) : undefined}
            </div>
          ) : undefined}

          <div class="mt-6">
            <ActionButton
              busyLabel={sendingCodeLabel}
              running={send.running}
              disabled={!sendArgs}
              onClick={sendArgs ? () => send.run(...sendArgs) : undefined}
            >
              {mode === "change" ? changeActionLabel : sendCodeLabel}
            </ActionButton>
          </div>

          {mode === "change" && canUsePreviousCode ? (
            <div class="mt-6 border-t border-outlineVariant pt-5 dark:border-darkSecondaryContainer">
              <p class="mb-3 text-sm text-secondary dark:text-darkSecondary">
                {previousCodeStillValid}
              </p>
              <SecondaryAction href={routeSolveChallenge.url({})}>
                {backToCodeLabel}
              </SecondaryAction>
            </div>
          ) : undefined}
        </>
      )}
    </VerificationCard>
  );
}

export function challengeFailureMessage(
  i18n: InternationalizationAPI,
  failure: {
    case: HttpStatusCode;
    detail?: { code?: number };
  },
): TranslatedString {
  if (
    failure.case === HttpStatusCode.ServiceUnavailable ||
    failure.detail?.code === TalerErrorCode.CHALLENGER_ADDRESS_UNREACHABLE
  ) {
    // Translators: Delivery failure for an unreachable email address, phone
    // number, or postal address; the user may edit it or choose another one.
    return i18n.str`The code could not be delivered to this destination. Check it or use a different one.`;
  }
  switch (failure.case) {
    case HttpStatusCode.BadRequest: {
      if (
        failure.detail?.code ===
        TalerErrorCode.CHALLENGER_ADDRESS_RESTRICTION_VIOLATED
      ) {
        // Translators: Address-validation error when one or more form fields
        // violate restrictions configured by the Challenger administrator.
        return i18n.str`This destination does not meet the service requirements. Check the highlighted fields.`;
      }
      // Translators: Generic validation error for an email address, phone
      // number, or postal address submitted to Challenger.
      return i18n.str`This destination could not be used. Check it and try again.`;
    }
    case HttpStatusCode.Forbidden:
      // Translators: The application fixed the verification address, so the
      // user is not permitted to edit or replace it.
      return i18n.str`This destination is fixed and cannot be changed.`;
    case HttpStatusCode.NotFound:
      // Translators: Recovery instruction after the current verification
      // session has expired.
      return i18n.str`This verification has expired. Return to the application and start again.`;
    case HttpStatusCode.NotAcceptable:
      // Translators: Server configuration error indicating that no delivery
      // helper can send the requested verification code.
      return i18n.str`The verification service is not configured to send this code.`;
    case HttpStatusCode.PayloadTooLarge:
      // Translators: Validation error for an excessively long email address,
      // phone number, or postal address.
      return i18n.str`The destination is too long. Shorten it and try again.`;
    case HttpStatusCode.UnsupportedMediaType:
      // Translators: Compatibility error between this web UI and Challenger.
      return i18n.str`The verification page and service are incompatible.`;
    case HttpStatusCode.TooManyRequests:
      // Translators: Rate-limit error after all permitted code deliveries have
      // been used for this verification session.
      return i18n.str`No more codes can be sent for this verification. Use a different destination or start again.`;
    case HttpStatusCode.BadGateway:
      // Translators: Temporary outage of the external email, SMS, or postal
      // delivery provider used by Challenger.
      return i18n.str`The delivery service is temporarily unavailable. Try again in a moment.`;
    case HttpStatusCode.InternalServerError:
      // Translators: Retry instruction after a temporary internal Challenger
      // failure while sending a verification code.
      return i18n.str`The verification service had a temporary problem. Try again.`;
    default:
      // Translators: Generic failure after Challenger could not send a
      // verification code for an unspecified reason.
      return i18n.str`The code could not be sent. Try again.`;
  }
}

export const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
export const INT_PHONE_REGEX = /^\+?[1-9]\d{6,14}$/; // E.164 International Phone Number
export const PHONE_REGEX = INT_PHONE_REGEX;

export const CONTACT_REGEX = /.*/;
export const ADDR_LINES_REGEX = /.*/;

export function getLocalizedRestrictionHint(
  serverConfig: ChallengerApi.Restriction | undefined,
  lang: string,
): string | undefined {
  const translated = serverConfig?.hint_i18n;
  const baseLang = lang.split(/[-_]/)[0];
  return (
    translated?.[lang] ??
    translated?.[baseLang] ??
    translated?.en ??
    serverConfig?.hint
  );
}

export function findInvalidRestriction(
  restrictions: Record<string, ChallengerApi.Restriction | undefined>,
): string | undefined {
  for (const [field, restriction] of Object.entries(restrictions)) {
    if (!restriction?.regex) continue;
    try {
      new RegExp(restriction.regex);
    } catch {
      return field;
    }
  }
  return undefined;
}

function getRestriction(
  i18n: InternationalizationAPI,
  lang: string,
  serverConfig: ChallengerApi.Restriction | undefined,
  fallback?: RegExp,
): { regex: undefined | RegExp; hint: TranslatedString } {
  const regexText =
    serverConfig && serverConfig.regex ? serverConfig.regex : undefined;
  // Translators: Actionable fallback validation message below an address form
  // field when Challenger does not provide a more specific localized hint.
  const invalidFieldMessage = i18n.str`Enter a valid value.`;
  const hint = (getLocalizedRestrictionHint(serverConfig, lang) ??
    invalidFieldMessage) as TranslatedString;

  let regex;
  if (regexText) {
    try {
      regex = new RegExp(regexText);
    } catch (e) {
      console.error(`Invalid server regular expression configuration. Server restriction
          is "${regexText}" but it didn't compile: ${String(e)}`);
      // return (
      //   <Attention title={i18n.str`Server configuration error`} type="danger">
      //     <i18n.Translate>
      //       Invalid server regular expression configuration. Server
      //       restriction is "{regexText}" but it didn't compile: {String(e)}
      //     </i18n.Translate>
      //   </Attention>
      // );
      regex = fallback;
    }
  } else {
    regex = fallback;
  }
  return { regex, hint };
}
function getFormDesignBasedOnAddressType(
  i18n: InternationalizationAPI,
  lang: string,
  type: ChallengerApi.ChallengerTermsOfServiceResponse["address_type"],
  restrictions: Record<string, ChallengerApi.Restriction | undefined>,
  read_only: boolean,
  changing: boolean,
): FormDesign {
  // Translators: Label for the initial email-address input where the user
  // wants to receive a verification code.
  const emailLabel = i18n.str`Email`;
  // Translators: Label for a prefilled email-address input that must be edited
  // to choose a replacement destination.
  const newEmailLabel = i18n.str`New email address`;
  // Translators: Label for the initial phone-number input where the user wants
  // to receive a verification code by SMS.
  const phoneLabel = i18n.str`Phone`;
  // Translators: Label for a prefilled phone-number input that must be edited
  // to choose a replacement destination.
  const newPhoneLabel = i18n.str`New phone number`;
  switch (type) {
    case "email":
      return {
        type: "single-column",
        fields: [
          {
            type: "text",
            id: TalerFormAttributes.CONTACT_EMAIL,
            required: true,
            label: changing ? newEmailLabel : emailLabel,
            disabled: read_only,
            validator(text) {
              const restriction = getRestriction(
                i18n,
                lang,
                restrictions[TalerFormAttributes.CONTACT_EMAIL],
                EMAIL_REGEX,
              );
              if (restriction.regex && !restriction.regex.test(text)) {
                return restriction.hint;
              }
              // const prev = prevValue[TalerFormAttributes.CONTACT_EMAIL];
              // if (prev === text) {
              //   return i18n.str`Can't use the same address`;
              // }
              return undefined;
            },
          },
        ],
      };
    case "phone":
      return {
        type: "single-column",
        fields: [
          {
            type: "phone",
            id: TalerFormAttributes.CONTACT_PHONE,
            required: true,
            label: changing ? newPhoneLabel : phoneLabel,
            disabled: read_only,
            validator(text) {
              const restriction = getRestriction(
                i18n,
                lang,
                restrictions[TalerFormAttributes.CONTACT_PHONE],
                PHONE_REGEX,
              );
              if (restriction.regex && !restriction.regex.test(text)) {
                return restriction.hint;
              }
              // const prev = prevValue[TalerFormAttributes.CONTACT_PHONE];
              // if (prev === text) {
              //   return i18n.str`Can't use the same number`;
              // }
              return undefined;
            },
          },
        ],
      };
    case "postal":
      return {
        type: "single-column",
        fields: [
          {
            type: "text",
            id: TalerFormAttributes.CONTACT_NAME,
            required: true,
            disabled: read_only,
            // Translators: Postal-address form label for the recipient's name;
            // this may name either a person or a business.
            label: i18n.str`Contact name`,
            // Translators: Placeholder in the postal recipient-name field.
            placeholder: i18n.str`Full name of the person or business`,
            validator(text) {
              const restriction = getRestriction(
                i18n,
                lang,
                restrictions[TalerFormAttributes.CONTACT_NAME],
                CONTACT_REGEX,
              );
              if (restriction.regex && !restriction.regex.test(text)) {
                return restriction.hint;
              }
              return undefined;
            },
          },
          {
            type: "textArea",
            id: TalerFormAttributes.ADDRESS_LINES,
            required: true,
            disabled: read_only,
            // Translators: Label for a multiline postal street-address field.
            label: i18n.str`Address`,
            // Translators: Three-line example for a postal-address text area.
            // Preserve the line breaks and keep the components in local
            // postal-address order.
            placeholder: i18n.str`Street and house number
Additional address information
Postal code and city`,
            validator(text) {
              const restriction = getRestriction(
                i18n,
                lang,
                restrictions[TalerFormAttributes.ADDRESS_LINES],
                ADDR_LINES_REGEX,
              );
              if (restriction.regex && !restriction.regex.test(text)) {
                return restriction.hint;
              }
              return undefined;
            },
          },
          {
            id: TalerFormAttributes.ADDRESS_COUNTRY,
            // Translators: Label for the country selector in a postal-address
            // verification form.
            label: i18n.str`Country`,
            type: "selectOne",
            disabled: read_only,
            choices: countryNameList(i18n),
            required: true,
            preferredChoiceVals: ["CH", "DE"],
            validator(text) {
              const restriction = getRestriction(
                i18n,
                lang,
                restrictions[TalerFormAttributes.ADDRESS_COUNTRY],
              );
              if (restriction.regex && !restriction.regex.test(text)) {
                return restriction.hint;
              }
              return undefined;
            },
          },
        ],
      };

    case "postal-ch":
      return {
        type: "single-column",
        fields: [
          {
            type: "text",
            id: TalerFormAttributes.CONTACT_NAME,
            required: true,
            disabled: read_only,
            // Translators: Postal-address form label for the recipient's name;
            // this may name either a person or a business.
            label: i18n.str`Contact name`,
            // Translators: Placeholder in the recipient-name field of the Swiss
            // postal-address form.
            placeholder: i18n.str`Your full name`,
            validator(text) {
              const restriction = getRestriction(
                i18n,
                lang,
                restrictions[TalerFormAttributes.CONTACT_NAME],
              );
              if (restriction.regex && !restriction.regex.test(text)) {
                return restriction.hint;
              }
              return undefined;
            },
          },
          {
            type: "textArea",
            id: TalerFormAttributes.ADDRESS_LINES,
            required: true,
            disabled: read_only,
            // Translators: Label for a multiline postal-address field restricted
            // to addresses in Switzerland.
            label: i18n.str`Swiss address`,
            // Translators: Help text below the Swiss postal-address field.
            help: i18n.str`Make sure this is a Swiss address.`,
            // Translators: Three-line example for a Swiss postal-address text
            // area. Preserve the line breaks and keep the components in local
            // postal-address order.
            placeholder: i18n.str`Street and house number
Additional address information
Postal code and city`,
            validator(text) {
              const restriction = getRestriction(
                i18n,
                lang,
                restrictions[TalerFormAttributes.ADDRESS_LINES],
              );
              if (restriction.regex && !restriction.regex.test(text)) {
                return restriction.hint;
              }
              return undefined;
            },
          },
          {
            id: TalerFormAttributes.ADDRESS_COUNTRY,
            // Translators: Label for the country field, fixed to Switzerland in
            // this version of the postal-address form.
            label: i18n.str`Country`,
            type: "selectOne",
            choices: countryNameList(i18n),
            required: true,
            disabled: true,
            preferredChoiceVals: ["CH"],
            validator(text) {
              const restriction = getRestriction(
                i18n,
                lang,
                restrictions[TalerFormAttributes.ADDRESS_COUNTRY],
              );
              if (restriction.regex && !restriction.regex.test(text)) {
                return restriction.hint;
              }
              return undefined;
            },
          },
        ],
      };
    default: {
      assertUnreachable(type);
    }
  }
}
